/**
 * Derives contextual signals for PlaceCore hero.
 * Season, time commitment, and experience type — inferred from tags + region.
 * These are soft signals, never hard facts.
 */
import type { FarmlyPlace } from "@/lib/types";

export interface PlaceSignals {
  season: string;
  timeEstimate: string;
  vibeType: string;
}

const OUTDOOR_TAGS = ["natur", "friluftsliv", "vandring", "fäbod", "landsbygd"];
const CULTURE_TAGS = ["kultur", "historia"];
const CALM_TAGS = ["lugn", "fäbod", "landsbygd"];

export function derivePlaceSignals(place: FarmlyPlace): PlaceSignals {
  const tags = place.tags.map((t) => t.toLowerCase());

  // Season: outdoor-heavy → spring/summer, culture → year-round
  const isOutdoor = tags.some((t) => OUTDOOR_TAGS.includes(t));
  const isCulture = tags.some((t) => CULTURE_TAGS.includes(t));
  const season = isCulture
    ? "Året runt"
    : isOutdoor
      ? "Bäst: vår–sommar"
      : "Bäst: vår–höst";

  // Time: calm/rural places → longer stay, others → half day
  const isCalm = tags.some((t) => CALM_TAGS.includes(t));
  const timeEstimate = isCalm ? "Halvdag–heldag" : "2–4 timmar";

  // Vibe: primary experiential signal
  const vibeType = isCalm
    ? "Lugn & stillhet"
    : isOutdoor
      ? "Natur & friluftsliv"
      : isCulture
        ? "Kultur & historia"
        : "Utflykt & upptäckt";

  return { season, timeEstimate, vibeType };
}

/**
 * Generates observational "vibe" text based on tags and region.
 * 3–5 sentences, never marketing, never factual claims.
 */
export function buildVibeText(place: FarmlyPlace): string {
  const region = place.municipality || place.landskap || "området";
  const tags = place.tags.map((t) => t.toLowerCase());

  const sentences: string[] = [];

  // Opening — tempo and feeling
  if (tags.includes("lugn") || tags.includes("fäbod")) {
    sentences.push(`${place.name} har en tydlig känsla av lugn. Tempot är lågt och omgivningen bjuder in till eftertanke.`);
  } else if (tags.includes("friluftsliv") || tags.includes("vandring")) {
    sentences.push(`${place.name} präglas av öppen natur och rörelsefrihet. Omgivningen uppmuntrar till att vara utomhus.`);
  } else if (tags.includes("kultur") || tags.includes("historia")) {
    sentences.push(`${place.name} bär spår av historia och lokal kultur. Det finns en känsla av sammanhang med platsen.`);
  } else {
    sentences.push(`${place.name} erbjuder en varierad upplevelse. Omgivningen i ${region} ger platsen dess karaktär.`);
  }

  // Season
  if (tags.includes("natur") || tags.includes("friluftsliv")) {
    sentences.push("Under vår och sommar öppnar landskapet upp sig och bjuder på ljus och grönska.");
  } else {
    sentences.push("Platsen kan upplevas under olika årstider, var och en med sin egen stämning.");
  }

  // Closing — atmosphere
  if (tags.includes("barnvänligt")) {
    sentences.push("Känslan passar även familjer — tempot tillåter alla att vara med.");
  }

  if (tags.includes("landsbygd")) {
    sentences.push("Landskapet omkring präglas av öppna fält, skog och det lokala livets rytm.");
  }

  // Fallback if too short
  if (sentences.length < 3) {
    sentences.push(`Besökare som söker sig till ${region} hittar här en plats att stanna till vid.`);
  }

  return sentences.join(" ");
}

/**
 * Generates 1–2 sentences of micro-facts for "Visste du att…".
 * Culture, geography, or historical context — always gentle.
 */
export function buildDidYouKnow(place: FarmlyPlace): string | null {
  const tags = place.tags.map((t) => t.toLowerCase());
  const region = place.landskap || place.county || null;

  if (tags.includes("fäbod")) {
    return `Fäbodar var en central del av det svenska jordbruket och användes som sommarbeten. ${place.name} ligger i ett område med anknytning till den traditionen.`;
  }

  if (tags.includes("kultur") || tags.includes("historia")) {
    return `Många platser i ${region || "Sverige"} har anknytning till äldre tiders kultur och samhällsliv. ${place.name} är en del av det lokala kulturlandskapet.`;
  }

  if (region) {
    return `${region} är ett av Sveriges landskap med karaktäristisk natur och lokala traditioner. ${place.name} är en av platserna som speglar regionens identitet.`;
  }

  return null;
}
